/* ============================================================
   SPICY DADDA, Interaction layer
   1. The Jump to Light Speed, canvas warp + white-blue flash
   2. The Fleet, drag-to-scroll carousel
   ============================================================
   Sections below the hero are normal, independently reorderable
   Shopify sections living in regular page flow (no scroll-lock,
   no fixed-position takeover) so the Theme Customizer can safely
   reorder/hide them. "Explore" plays the warp flash, then smooth-
   scrolls into the Fleet section as a nice-to-have, not a gate. */

(() => {
  'use strict';

  const launchBtn  = document.getElementById('launchBtn');
  const fleet      = document.getElementById('fleet');
  const canvas     = document.getElementById('warpCanvas');
  const carousel   = document.getElementById('carousel');
  const homeLogo   = document.getElementById('homeLogo'); // header logo → return home
  const ctx        = canvas.getContext('2d');

  const lerp = (a, b, t) => a + (b - a) * t;

  /* ------------------------------------------------------------
     0. HERO VIDEO, robust muted autoplay
     Some browsers leave a muted <video autoplay> paused until nudged
     (backgrounded tab, restored page, autoplay policy). Keep trying.
     ------------------------------------------------------------ */
  document.querySelectorAll('.overture__video').forEach(heroVideo => {
    const tryPlay = () => { const p = heroVideo.play(); if (p) p.catch(() => {}); };
    tryPlay();
    heroVideo.addEventListener('canplay', tryPlay, { once: true });
    window.addEventListener('pageshow', tryPlay);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) tryPlay(); });
    document.addEventListener('pointerdown', tryPlay, { once: true });
  });

  const prefersReducedMotion =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let launching = false;


  /* ------------------------------------------------------------
     0b. HOME SCROLL LOCK, hold the page on the hero until launch
     Only engages on the homepage (where #overture/#launchBtn exist),
     no-op on every other page. Released the moment the warp starts.
     ------------------------------------------------------------ */
  let scrollLocked = false;
  if (document.getElementById('overture') && launchBtn) {
    scrollLocked = true;
    document.body.style.overflow = 'hidden';

    const blockScroll = (e) => { if (scrollLocked) e.preventDefault(); };
    window.addEventListener('wheel', blockScroll, { passive: false });
    window.addEventListener('touchmove', blockScroll, { passive: false });
    window.addEventListener('keydown', (e) => {
      if (!scrollLocked) return;
      const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
      if (scrollKeys.includes(e.key)) e.preventDefault();
    });
  }

  // Arriving via a #fleet link (header logo / nav "Home") should land
  // straight on the post-warp homepage, not replay the pre-warp intro.
  if (window.location.hash === '#fleet' && document.getElementById('overture') && launchBtn) {
    scrollLocked = false;
    document.body.style.overflow = '';
    document.body.classList.add('is-arrived');
    canvas.classList.add('is-hidden');
    if (homeLogo) homeLogo.classList.remove('is-hidden');
    requestAnimationFrame(() => window.scrollTo(0, 0));
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }


  /* ------------------------------------------------------------
     1. WARP STARFIELD ENGINE
     ------------------------------------------------------------ */
  const STAR_COUNT = 720;
  let stars = [];
  let dpr = Math.min(window.devicePixelRatio || 1, 2);

  function sizeCanvas() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width  = Math.floor(window.innerWidth  * dpr);
    canvas.height = Math.floor(window.innerHeight * dpr);
  }

  function seedStars() {
    const w = canvas.width, h = canvas.height;
    stars = new Array(STAR_COUNT).fill(0).map(() => ({
      x: (Math.random() - 0.5) * w,
      y: (Math.random() - 0.5) * h,
      z: Math.random() * w,
      pz: 0,
    }));
    // prime previous-z so the first frame draws clean points, not rogue streaks
    stars.forEach(s => (s.pz = s.z));
  }

  // colorMix: 0 = full blue-white starlight, 1 = fully desaturated & gone.
  // bgAlpha:  per-frame black wipe; climbs to 1 to fill the field with void.
  function renderWarp(speed, colorMix, bgAlpha) {
    const w = canvas.width, h = canvas.height;
    const cx = w / 2, cy = h / 2;

    // Translucent black wipe → motion-blur trails (the "stretch").
    // As we drop out of lightspeed bgAlpha rises to 1, filling the screen
    // with pure deep-space black, no white flash, ever.
    ctx.fillStyle = `rgba(11, 11, 11, ${bgAlpha})`;
    ctx.fillRect(0, 0, w, h);

    ctx.lineCap = 'round';

    for (const s of stars) {
      s.pz = s.z;
      s.z -= speed;

      // Recycle stars that pass the camera
      if (s.z < 1) {
        s.x = (Math.random() - 0.5) * w;
        s.y = (Math.random() - 0.5) * h;
        s.z = w;
        s.pz = s.z;
      }

      const k  = w;                       // projection scale
      const sx = (s.x / s.z) * k + cx;    // current screen pos
      const sy = (s.y / s.z) * k + cy;
      const px = (s.x / s.pz) * k + cx;   // previous screen pos
      const py = (s.y / s.pz) * k + cy;

      const depth = 1 - s.z / w;          // 0 (far) → 1 (near)
      ctx.lineWidth = Math.max(0.4, depth * 3.2);

      // Blue-white at speed; desaturate toward dark grey, then fade to nothing
      // as we drop into the void (colorMix 0 → 1).
      const r = lerp(200 + depth * 55, 60, colorMix);
      const g = lerp(225 + depth * 30, 60, colorMix);
      const b = lerp(255,              60, colorMix);
      const a = (0.35 + depth * 0.65) * (1 - colorMix);

      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${a})`;
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(sx, sy);
      ctx.stroke();
    }
  }


  /* ------------------------------------------------------------
     2b. LAUNCH SEQUENCE (timeline)
     ------------------------------------------------------------ */
  // Accelerate into light streaks, then keep streaking WHILE the canvas
  // crossfades into The Fleet, the motion never freezes, it dissolves.
  const ACCEL_MS = 600;        // drift → light-speed streaks
  const FADE_MS  = 650;        // streaks keep flying as they crossfade away

  // Smooth-scroll down into the Fleet section, the hand-off point from the
  // hero into the rest of the page (now normal, independently scrollable flow).
  function revealFleet() {
    if (homeLogo) homeLogo.classList.remove('is-hidden');
    if (fleet) fleet.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  }

  // Begin the crossfade: reveal the Fleet behind the still-opaque canvas and
  // start fading the streaks out. The starfield keeps animating underneath the
  // fade so the hand-off stays in continuous motion.
  function beginReveal() {
    document.body.classList.remove('is-warping');
    document.body.classList.add('is-arrived');   // fleet content fades up + hides overture
    canvas.classList.add('is-hidden');           // streaks crossfade away
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);                    // fleet is now top of page
      if (homeLogo) homeLogo.classList.remove('is-hidden');
    });
  }

  // Return Home: scroll to the top of the post-warp homepage. Only ever
  // wired to the logo, which only shows once already arrived, so this
  // must never re-arm the pre-warp intro.
  function returnHome() {
    setMenu(false);
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  }

  function endWarp() {
    canvas.classList.remove('is-active', 'is-hidden');
    launching = false;
  }

  function runWarp() {
    sizeCanvas();
    seedStars();
    canvas.classList.add('is-active');

    let startTime = null;
    let revealing = false;

    function frame(now) {
      if (startTime === null) startTime = now;
      const t = now - startTime;

      if (t <= ACCEL_MS) {
        // Accelerate into light speed (blue-white streaks)
        const p = t / ACCEL_MS;
        renderWarp(6 + Math.pow(p, 3) * 150, 0, 0.25);
        requestAnimationFrame(frame);
      } else {
        // Crossfade phase, keep the streaks flying while the canvas fades,
        // so it dissolves into the product instead of cutting.
        if (!revealing) { revealing = true; beginReveal(); }
        const ft = t - ACCEL_MS;
        renderWarp(156 + ft * 0.05, 0, 0.25);   // sustain speed → no freeze
        if (ft < FADE_MS) requestAnimationFrame(frame);
        else endWarp();
      }
    }
    requestAnimationFrame(frame);
  }

  function initiateLaunch() {
    if (launching) return;
    launching = true;
    launchBtn.blur();
    document.body.classList.add('is-warping');   // hold the hangar dark
    scrollLocked = false;
    document.body.style.overflow = '';

    if (prefersReducedMotion) {
      // Spectacle off: a quick cut straight to the Fleet
      revealFleet();
      document.body.classList.remove('is-warping');
      document.body.classList.add('is-arrived');
      launching = false;
      return;
    }

    runWarp();
  }

  launchBtn.addEventListener('click', initiateLaunch);

  window.addEventListener('resize', () => {
    if (canvas.classList.contains('is-active')) sizeCanvas();
  });


  /* ------------------------------------------------------------
     2c. HEADER HAMBURGER MENU
     ------------------------------------------------------------ */
  const menuToggle = document.getElementById('menuToggle');
  const menuClose  = document.getElementById('menuClose');
  const appMenu    = document.getElementById('appMenu');

  const setMenu = (open) => {
    if (!appMenu) return;
    appMenu.classList.toggle('is-open', open);
    appMenu.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (menuToggle) menuToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  if (menuToggle) menuToggle.addEventListener('click', () => setMenu(true));
  if (menuClose)  menuClose.addEventListener('click', () => setMenu(false));
  if (appMenu) {
    appMenu.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link && link.dataset.nav === 'home') {
        if (document.body.classList.contains('is-arrived')) {
          e.preventDefault();
          setMenu(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }
      if (link || e.target === appMenu) setMenu(false);
    });
  }
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setMenu(false); });

  /* ------------------------------------------------------------
     2d. RETURN HOME + scroll-reactive header logo
     ------------------------------------------------------------ */
  if (homeLogo) homeLogo.addEventListener('click', (e) => { e.preventDefault(); returnHome(); });

  // Fade the header logo out once scrolled 50px past the fleet section.
  if (homeLogo && fleet) {
    window.addEventListener('scroll', () => {
      homeLogo.classList.toggle('is-hidden', window.scrollY > fleet.offsetTop + 50);
    }, { passive: true });
  }

  /* ------------------------------------------------------------
     2e. FOOTER NEWSLETTER, acknowledge the signal
     ------------------------------------------------------------ */
  const footerNews = document.getElementById('footerNews');
  if (footerNews) {
    footerNews.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = footerNews.querySelector('.footer-news__btn');
      const input = footerNews.querySelector('.footer-news__input');
      if (input) { input.value = ''; input.placeholder = 'Frequency locked ✓'; input.disabled = true; }
      if (btn) { btn.textContent = 'Joined'; btn.disabled = true; }
    });
  }


  /* ------------------------------------------------------------
     3. CAROUSEL, native swipe (touch) + mouse-drag (desktop)
     Touch is left to the browser (overflow-x + scroll-snap) so swipes feel
     native; we only add mouse-drag for desktop pointers.
     ------------------------------------------------------------ */
  let isDown = false;
  let startX = 0;
  let startScroll = 0;
  let moved = false;

  const pointerDown = (clientX) => {
    isDown = true;
    moved = false;
    startX = clientX;
    startScroll = carousel.scrollLeft;
  };

  const pointerMove = (clientX) => {
    if (!isDown) return;
    const dx = clientX - startX;
    if (Math.abs(dx) > 4) {
      moved = true;
      carousel.classList.add('is-dragging');  // disables snap mid-drag
    }
    carousel.scrollLeft = startScroll - dx;
  };

  const pointerUp = () => {
    isDown = false;
    requestAnimationFrame(() => carousel.classList.remove('is-dragging'));
  };

  // Mouse only, touch swipes are handled natively
  carousel.addEventListener('mousedown', (e) => pointerDown(e.clientX));
  window.addEventListener('mousemove', (e) => { if (isDown) { e.preventDefault(); pointerMove(e.clientX); } }, { passive: false });
  window.addEventListener('mouseup', pointerUp);

  // Suppress a click that was actually a drag
  carousel.addEventListener('click', (e) => {
    if (moved) { e.preventDefault(); e.stopPropagation(); }
  }, true);

  // Translate vertical-only wheel scroll into horizontal traversal, for mice
  // with no horizontal axis. Trackpad horizontal swipes already arrive as
  // native deltaX and the browser scrolls the carousel for those on its own;
  // adding to scrollLeft here too would double the movement and feel erratic.
  carousel.addEventListener('wheel', (e) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      carousel.scrollLeft += e.deltaY;
    }
  }, { passive: true });


  /* ------------------------------------------------------------
     4. ENDLESS CAROUSEL, clone-wrapped loop + directional nebula
     The N real cards are flanked by a full clone copy on each side, so the
     track can be swiped forever in either direction. Whenever the centred
     card lands in a clone zone we silently snap scrollLeft onto the matching
     real card (identical pixels, no visible jump). Whichever card is centred
     gets .is-active, and its nebula slides in from the side OPPOSITE the
     swipe direction (counter-parallax) via the --env-from custom property.
     ------------------------------------------------------------ */
  const realCards = Array.from(carousel.querySelectorAll('.ship-card'));
  const N = realCards.length;
  let cards = realCards;

  if (N > 1) {
    const headClones = realCards.map((c) => c.cloneNode(true));   // before real[0]
    const tailClones = realCards.map((c) => c.cloneNode(true));   // after real[N-1]
    headClones.forEach((c) => carousel.insertBefore(c, realCards[0]));
    tailClones.forEach((c) => carousel.appendChild(c));
    cards = Array.from(carousel.querySelectorAll('.ship-card'));   // 3N nodes
  }

  const pitch = () =>
    cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : carousel.clientWidth;
  const centredIndex = () => Math.round(carousel.scrollLeft / pitch());

  let activeNode = null;
  let lastDir = 1;   // 1 = forward (scrollLeft increasing), -1 = back

  const setActive = (card) => {
    if (!card || card === activeNode) return;
    // Nebula enters from the side OPPOSITE the swipe direction.
    const env = card.querySelector('.ship-card__env');
    if (env) env.style.setProperty('--env-from', lastDir >= 0 ? '-40%' : '40%');
    cards.forEach((c) => c.classList.toggle('is-active', c === card));
    activeNode = card;
  };

  if ('IntersectionObserver' in window && cards.length) {
    const centreObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActive(entry.target);
      });
    }, {
      root: carousel,
      rootMargin: '0px -49.5% 0px -49.5%',  // collapse root to a centre line
      threshold: 0
    });
    cards.forEach((c) => centreObserver.observe(c));
  }

  // Endless wrap: jump out of the clone zones onto the matching real card.
  let isLooping = false;
  const loopFix = () => {
    if (isLooping || N < 2) return;
    const ci = centredIndex();
    if (ci >= N && ci < 2 * N) return;               // already on a real card
    isLooping = true;
    const realIndex = ci < N ? ci + N : ci - N;
    carousel.classList.add('is-looping');            // freeze fades for the swap
    carousel.scrollLeft += (realIndex - ci) * pitch();
    setActive(cards[realIndex]);                     // keep the nebula revealed
    requestAnimationFrame(() => {
      carousel.classList.remove('is-looping');
      isLooping = false;
    });
  };

  // Track scroll direction + run the wrap once scrolling settles.
  let prevSL = carousel.scrollLeft;
  let scrollIdle;
  carousel.addEventListener('scroll', () => {
    if (!isLooping) {
      const sl = carousel.scrollLeft;
      if (sl > prevSL) lastDir = 1;
      else if (sl < prevSL) lastDir = -1;
      prevSL = sl;
    } else {
      prevSL = carousel.scrollLeft;
    }
    clearTimeout(scrollIdle);
    scrollIdle = setTimeout(loopFix, 90);
  }, { passive: true });
  carousel.addEventListener('scrollend', loopFix);


  /* ------------------------------------------------------------
     5. NAV ARROWS, telemetry chevrons (step one card; wrap handles loop)
     ------------------------------------------------------------ */
  const step = (dir) => {
    lastDir = dir;
    carousel.scrollBy({ left: dir * pitch(), behavior: 'smooth' });
  };
  const navPrev = document.querySelector('.deck-nav--prev');
  const navNext = document.querySelector('.deck-nav--next');
  if (navPrev) navPrev.addEventListener('click', () => step(-1));
  if (navNext) navNext.addEventListener('click', () => step(1));

  // Start centred on the first REAL card and light it up.
  if (N > 1) {
    carousel.scrollLeft = N * pitch();
    prevSL = carousel.scrollLeft;
  }
  setActive(cards[N > 1 ? N : 0]);


  /* ------------------------------------------------------------
     6. FIRST CONTACT BUNDLE, reveal on scroll-in (separate observer)
     Mirrors the product reveal: the background slides in and the product
     pops up. The product-card observer above is left untouched. Root is
     the viewport (normal page scroll) so it fires as the bundle scrolls
     into view.
     ------------------------------------------------------------ */
  const bundleCard = document.getElementById('bundleCard');
  const bundleBioFrames = document.querySelectorAll('.bundle__bio-frame');
  if (bundleCard && 'IntersectionObserver' in window) {
    const bundleObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        bundleCard.classList.toggle('is-revealed', entry.isIntersecting);
        bundleBioFrames.forEach((frame) => frame.classList.toggle('is-revealed', entry.isIntersecting));
      });
    }, { threshold: 0.35 });
    bundleObserver.observe(bundleCard);
  } else if (bundleCard) {
    bundleCard.classList.add('is-revealed');
    bundleBioFrames.forEach((frame) => frame.classList.add('is-revealed'));
  }

})();


/* ============================================================
   PHASE 1 POLISH, strip periods from the prewarp tagline sequence
   "Homegrown. Fermented. Intergalactic." → "Homegrown Fermented Intergalactic"
   ============================================================ */
(() => {
  const tagline = document.querySelector('.overture__tagline');
  if (tagline) {
    tagline.textContent = tagline.textContent.replace(/\./g, '').replace(/\s+/g, ' ').trim();
  }
})();


/* ============================================================
   PHASE 1 POLISH, global footer restructure
   Kept in JS so the shared footer markup isn't rewritten per page.
   Runs on every page that loads this script.
   ============================================================ */
(() => {
  const footer = document.querySelector('.site-footer');
  if (!footer) return;
  const cols = footer.querySelectorAll('.footer-col');
  const social = footer.querySelector('.footer-social');

  // 1. Newsletter column → headline copy (enlarged via CSS), drop the
  //    secondary body line, and tuck social icons under the input bar.
  const nlCol = cols[0];
  if (nlCol) {
    const head = nlCol.querySelector('.footer-col__eyebrow');
    if (head) {
      head.textContent = window.matchMedia('(min-width: 1024px)').matches
        ? 'Join our mailing list for 15% off your next order.'
        : 'Join our mailing list for 15% off your next order';
      head.classList.add('footer-news__headline');
    }
    const body = nlCol.querySelector('.footer-col__body');
    if (body) body.remove();
    const form = nlCol.querySelector('.footer-news');
    if (form && social) form.insertAdjacentElement('afterend', social);
  }

  // 2. Shipping & Operations column → removed entirely (per punch-list).
  if (cols[1]) cols[1].remove();

  // 3. Comms Array column → removed (title gone, icons relocated above).
  if (cols[2]) cols[2].remove();
})();
